import { useCallback, useEffect, useMemo, useState } from 'react'
import { useToast } from '@bonitour/components'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQuery } from '@tanstack/react-query'
import { useTimeExpiration } from '@bonitour/app-functions'
import { identity } from '@bonitour/common-functions'
import { TransactionExportSchema } from '../TransactionExportModal/TransactionExport.schema'
import { useUser } from 'Shared/contexts/User'
import { TransactionService } from 'Core/Service/Transaction'

const EXPORT_OPTIONS = [
  { label: 'CSV', value: 'csv' },
  { label: 'XLSX', value: 'xlsx' }
]

const exportKey = 'transactionsColumnsToExport'

export const useTransactionExport = (transactionsFilters = {}, handleCloseExportModal = identity) => {
  const { add: addToast } = useToast()
  const { user } = useUser()

  const [newEmail, setNewEmail] = useState('')
  const [isLoadingExport, setIsLoadingExport] = useState(false)
  const [initialFormValues] = useState({
    wantedFields: [],
    emails: [user.email],
    format: 'csv'
  })

  const {
    data: columnsToExport = []
  } = useQuery({
    queryFn: () => {
      return TransactionService.listReportColumnsToExport()
    },
    queryKey: [exportKey],
    retry: 2
  })

  const {
    addTimer,
    isExpired,
    isStarted
  } = useTimeExpiration(30, exportKey)

  const {
    formState: { errors, isSubmitted },
    setValue,
    handleSubmit,
    watch,
    reset,
    trigger
  } = useForm({
    resolver: yupResolver(TransactionExportSchema),
    defaultValues: initialFormValues,
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const wantedFields = watch('wantedFields')
  const format = watch('format')
  const emails = watch('emails')

  const getParsedErrors = () => {
    return Object.keys(errors).reduce((acc, key) => {
      const error = errors[key]
      if (error) {
        if (typeof error === 'string') {
          acc[key] = error
        } else if (Array.isArray(error)) {
          acc[key] = error
        } else if (error?.message) {
          acc[key] = error.message
        }
      }
      return acc
    }, {})
  }

  const parsedErrors = getParsedErrors()

  const onSuccess = useCallback((formData) => {
    if (isLoadingExport || (!isExpired && isStarted)) return
    setIsLoadingExport(true)
    return TransactionService.exportReport({
      ...formData,
      filters: transactionsFilters || {}
    }).then(() => {
      addTimer()
      addToast('Relatório será enviado para seu e-mail e demais destinatários', 'success')
      reset({
        ...initialFormValues,
        wantedFields: columnsToExport.map((item) => item.value)
      })
      handleCloseExportModal()
    }).catch(() => {
      addToast('Houve um erro inesperado ao exportar')
    }).finally(() => {
      setIsLoadingExport(false)
    })
  }, [addTimer, addToast, columnsToExport, handleCloseExportModal, initialFormValues, isExpired, isLoadingExport, isStarted, reset, transactionsFilters])

  const onFailure = useCallback(() => {
    addToast('Houve um erro ao exportar as transações, confira o formulário e tente novamente')
  }, [addToast])

  const onInputChange = useCallback((field) => async (value) => {
    setValue(field, value)
    if (isSubmitted) {
      trigger(field)
    }
  }, [isSubmitted, setValue, trigger])

  const handleEmails = useCallback(data => {
    if (typeof data === 'function') {
      onInputChange('emails')(data(emails || []))
    } else {
      onInputChange('emails')(data)
    }
  },
  [emails, onInputChange])

  const onFormSubmit = handleSubmit(onSuccess, onFailure)

  const isChecked = useCallback((column) => {
    return (wantedFields || []).includes(columnsToExport.find((item) => item.label === column)?.value)
  }, [columnsToExport, wantedFields])

  const onOptionChange = useCallback((column) => () => {
    const value = columnsToExport.find((item) => item.label === column)?.value
    if (isChecked(column)) {
      return setValue('wantedFields', wantedFields.filter((item) => item !== value))
    }
    setValue('wantedFields', [...wantedFields, value])
  }, [columnsToExport, isChecked, setValue, wantedFields])

  const isSelectedAll = useMemo(() => {
    return Object.keys(columnsToExport).length === wantedFields?.length
  }, [columnsToExport, wantedFields?.length])

  const onSelectAll = useCallback(() => {
    if (isSelectedAll) {
      return setValue('wantedFields', [])
    }
    const newWantedFields = columnsToExport.map((item) => item.value)
    setValue('wantedFields', newWantedFields)
  }, [columnsToExport, isSelectedAll, setValue])

  const resetForm = useCallback(() => {
    reset({
      ...initialFormValues,
      wantedFields: columnsToExport.map((item) => item.value)
    })
  }
  , [columnsToExport, initialFormValues, reset])

  useEffect(() => {
    setValue('wantedFields', columnsToExport.map((item) => item.value))
  }, [columnsToExport, setValue])

  return {
    format,
    errors: parsedErrors,
    onOptionChange,
    isChecked,
    onSelectAll,
    isSelectedAll,
    onExport: onFormSubmit,
    setValue,
    onInputChange,
    reset: resetForm,
    columnsToExport,
    exportOptions: EXPORT_OPTIONS,
    emails,
    handleEmails,
    isLoadingExport,
    isExpired,
    isStarted,
    newEmail,
    setNewEmail
  }
}
